import React, { useState, useEffect, useCallback, useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { Bar } from "react-chartjs-2";
import { getCorporationFeeChartDataAction } from "../../Actions/PaycentralAction";
import NoDataFound from "./NoDataFound";
import { MyCancelIcon } from "../../Assets/svg/MyIcons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const CorporationFeeGraph = ({
  selectedMerchant,
  startDate,
  endDate,
  showButton,
  onRemove,
  onShowSnackbar,
}) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });

  const [loading, setLoading] = useState(false);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
      title: { display: true, text: "Corporation Fee Activity" },
    },
    scales: {
      x: { title: { display: true, text: "Month" } },
      y: {
        title: {
          display: true,
          text: "Amount",
        },
        beginAtZero: true,
      },
    },
  }), []);

  const fetchChartData = useCallback(async () => {
    setLoading(true);

    try {
      const pId = selectedMerchant?.pId || "all";

      const res = await getCorporationFeeChartDataAction({
        startDate,
        endDate,
        pId,
      });
      // console.log("dataaaaaaaaaaa", res)
      const labels = res?.data?.labels || [];
      const feeData = res?.data?.datasets || {};
      const datasets = [
        {
          label: "One Time Setup",
          data: feeData.oneTimeSetupFee || [],
          backgroundColor: "#1E88E5", // blue
        },
        {
          label: "Ongoing Maintenance",
          data: feeData.ongoingMaintenanceFee || [],
          backgroundColor: "#43A047", // green
        },
        {
          label: "New Merchant Setup",
          data: feeData.newMerchantSetupFee || [],
          backgroundColor: "#FB8C00", // orange
        },
        {
          label: "Per Merchant Fee",
          data: feeData.ongoingPerMerchantFee || [],
          backgroundColor: "#F48FB1", // pink (matches palette style)
        },
      ];

      setChartData({
        labels,
        datasets,
      });

    } catch (error) {
      console.error(error);
      onShowSnackbar("Failed to load fee chart", "error");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedMerchant, onShowSnackbar]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchChartData();
    }
  }, [startDate, endDate, selectedMerchant, fetchChartData]);

  const isNoData =
    !chartData.datasets.length ||
    chartData.datasets.every(ds =>
      !ds.data.length || ds.data.every(v => v === 0)
    );

  return (
    <div className={`kpi-card ${!showButton && "my-3"}`}>
      {showButton && (
        <MyCancelIcon
          className="close-icon position-absolute cursor-pointer dash-close z-index-3"
          style={{ top: 5, right: 5 }}
          onClick={() => onRemove("corporationFeeChart")}
        />
      )}

      <div className="kpi-card-content">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="chart-title m-0">Corporation Fees</h2>
        </div>

        <div className="chart-canvas-container" style={{ height: "350px" }}>
          {loading ? (
            <div className="chart-loading">
              <CircularProgress />
              <p>Loading fee data...</p>
            </div>
          ) : isNoData ? (
            <NoDataFound message="No fee data available" />
          ) : (
            <Bar data={chartData} options={chartOptions} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CorporationFeeGraph;